import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserInfo, UserRegistration, CustomerUserType, ManagerCompany } from '@model';
import { environment } from 'environments/environment';
import { SxErrorService } from 'app/services/sx-error.service';
import { UserRegistrationStatus } from 'app/models/enums/user-registration-status';
import { ValidationUserCustomerOneSegment } from 'app/models/validation-user-customer-one-segment.model';

@Injectable()
export class UserInfoService {

  constructor(private http: HttpClient, private sxErrorService: SxErrorService) {}

  getUserInfo(): Observable<UserInfo> {
    return this.http
      .get<UserInfo>(`${environment.apiEndpoint}/Account/Userinfo`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  updateUserInfo(payload: UserInfo): Observable<UserInfo> {
    return this.http
      .post<UserInfo>(`${environment.apiEndpoint}/Account/Userinfo`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  updateUserRegistration(payload: UserRegistration): Observable<UserRegistration> {
    return this.http
      .post<UserRegistration>(`${environment.apiEndpoint}/Account/InitialRegistration`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  inactivateUserAccount(payload: UserInfo): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/Inactivate`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  autoUserRegistration(payload: UserRegistration): Observable<UserRegistration> {
    return this.http
      .post<UserRegistration>(`${environment.apiEndpoint}/Account/AutoRegistration`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  addCustomerToManagerCompany(payload: UserRegistration): Observable<UserRegistration> {
    return this.http
      .post<UserRegistration>(
        `${environment.apiEndpoint}/ManagerCompanies/${payload.managerCompanyId}/Customers`,
        payload
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsCustomerUserTypeValid(payload: CustomerUserType): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsCustomerValid`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsUserCustomerValid(customerId: string): Observable<boolean>{
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsUserCustomerValid/${customerId}`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsUserCustomerOneSegmentValid(customerId: string): Observable<ValidationUserCustomerOneSegment>{
    return this.http
      .post<ValidationUserCustomerOneSegment>(`${environment.apiEndpoint}/Account/IsUserCustomerOneSegmentValid/${customerId}`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsCustomerIdAndOrgNoValid(customerId: string, orgNo: string){
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsCustomerIdOrgNoValid`, { customerId: customerId, orgNo: orgNo })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsCustomerMeteringPointsValid(customerId: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsCustomerMeteringPointsValid/${customerId}`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
  getIsManagerCompanyOrganisationNumberAlsoCustomer(orgNr: string): Observable<boolean> {
    return this.http
    .post<boolean>(`${environment.apiEndpoint}/Account/IsManagerCompanyOrganisationNumberAlsoCustomer/${orgNr}`, null)
    .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
  getIsRegisteredOnSegmentValid(customerId: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsRegisteredOnSegment/${customerId}`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
  getIsRegisteredOnCompanyValid(orgNr: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IsRegisteredOnCompany/${orgNr}`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  createManagerCompany(payload: ManagerCompany): Observable<ManagerCompany> {
    return this.http
      .post<ManagerCompany>(`${environment.apiEndpoint}/Account/InitialManagerRegistration`, payload)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsUserRegistered(email: string): Observable<UserRegistrationStatus> {
    return this.http
      .post<UserRegistrationStatus>(`${environment.apiEndpoint}/Account/IsUserRegistered`, { Email:email})
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getCustomerUserType(customerId: string): Observable<number> {
    return this.http
      .get<number>(`${environment.apiEndpoint}/Account/CustomerSegment/${customerId}`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getHasUserInfoExpired(): Observable<boolean> {
    return this.http
      .get<boolean>(`${environment.apiEndpoint}/Account/HasUserInfoExpired`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsUserMarkedForInactivation(userId: string): Observable<boolean> {
    return this.http
      .get<boolean>(`${environment.apiEndpoint}/Account/UserInfo/${userId}/IsUserMarkedForInactivation`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getCanUserBeInactivated(): Observable<boolean> {
    return this.http
      .get<boolean>(`${environment.apiEndpoint}/Account/CanUserBeInactivated`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  updateUserInfoExpirationDate(userId: string): Observable<UserInfo> {
    return this.http
      .post<UserInfo>(`${environment.apiEndpoint}/Account/UserInfo/${userId}/ExpirationDate`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
  sendVerificationEmail() {
    return this.http
    .get<UserInfo>(`${environment.apiEndpoint}/Account/SendVerificationEmail`)
    .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  setIntelligyLogin(){
    return this.http
      .post<boolean>(`${environment.apiEndpoint}/Account/IntelligyLogin`, null)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  setStartupApplication(startupApplication: number){
    return this.http
      .post<number>(`${environment.apiEndpoint}/Account/StartupApplication`, { startupApplication:  startupApplication})
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
}
